import { MedplumClient } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";

import { PostpartumEncounterDrawerActions } from "@/encounters/constants/postpartum-encounter";
import { AppDispatch } from "@/store";
import { updatePostpartumEncounterQuestionnaireResponse } from "@/store/slices/postpartum-encounter";

/**
 * Extracts nested answers from a QuestionnaireResponse structure
 * Handles the common pattern of nested questionnaire items with answers
 */
export const extractNestedAnswers = (
  questionnaireResponse: QuestionnaireResponse,
): Record<string, string[]> => {
  const answers: Record<string, string[]> = {};

  if (!questionnaireResponse.item) return answers;

  for (const topLevelItem of questionnaireResponse.item) {
    // Handle top-level answers (like "notes")
    if (topLevelItem.answer && topLevelItem.answer.length > 0) {
      const values = topLevelItem.answer
        .map(
          (answer) =>
            answer.valueString ||
            answer.valueDecimal?.toString() ||
            answer.valueInteger?.toString() ||
            answer.valueBoolean?.toString() ||
            answer.valueCoding?.display ||
            "",
        )
        .filter(Boolean);
      answers[topLevelItem.linkId] = values;
    }

    // Handle nested answers (like "UrineNormal", "NumberOfTimes", etc.)
    if (topLevelItem.item) {
      for (const questionItem of topLevelItem.item) {
        if (questionItem.answer && questionItem.answer.length > 0) {
          const values = questionItem.answer
            .map(
              (answer) =>
                answer.valueString ||
                answer.valueDecimal?.toString() ||
                answer.valueInteger?.toString() ||
                answer.valueBoolean?.toString() ||
                answer.valueCoding?.display ||
                "",
            )
            .filter(Boolean);
          answers[questionItem.linkId] = values;
        }
      }
    }
  }

  return answers;
};

/**
 * Generic function to handle Redux updates for postpartum encounter questionnaire responses
 * This eliminates the repetitive handleUpdateRedux pattern across drawer components
 */
export const handlePostpartumQuestionnaireReduxUpdate = <T extends Record<string, unknown>>({
  values,
  questionnaireName,
  getQuestionnaireResponse,
  clientId,
  practitionerId,
  encounterId,
  questionnaireResponse,
  dispatch,
}: {
  values: T;
  questionnaireName: string;
  getQuestionnaireResponse: (params: {
    form: T;
    clientId: string;
    practitionerId: string;
    encounterId: string;
    questionnaireId: string;
    status: QuestionnaireResponse["status"];
    questionnaireResponse?: QuestionnaireResponse;
  }) => QuestionnaireResponse;
  clientId: string;
  practitionerId: string;
  encounterId: string;
  questionnaireResponse?: QuestionnaireResponse;
  dispatch: AppDispatch;
}) => {
  const updatedQuestionnaireResponse = getQuestionnaireResponse({
    form: values,
    clientId,
    practitionerId,
    encounterId,
    questionnaireId: questionnaireResponse?.questionnaire?.split("/")[1] ?? "",
    status: questionnaireResponse?.status ?? "in-progress",
    questionnaireResponse,
  });

  dispatch(
    updatePostpartumEncounterQuestionnaireResponse({
      questionnaireName,
      questionnaireResponse: updatedQuestionnaireResponse,
    }),
  );
};

/**
 * Determines the questionnaire response status based on encounter state and last action
 * This eliminates the repetitive getValue pattern across drawer components
 */
export const getQuestionnaireResponseStatus = (
  isEncounterActive: boolean,
  lastAction: PostpartumEncounterDrawerActions | undefined,
): QuestionnaireResponse["status"] => {
  if (!isEncounterActive) return "amended";
  return lastAction === PostpartumEncounterDrawerActions.Save ? "in-progress" : "completed";
};

/**
 * Searches for existing questionnaire responses for a patient/encounter/questionnaire combination
 * This eliminates the repetitive search pattern across service functions
 */
export const findExistingQuestionnaireResponse = async (
  medplum: MedplumClient,
  clientId: string,
  encounterId: string,
  questionnaireId: string,
): Promise<QuestionnaireResponse | undefined> => {
  return await medplum.searchOne("QuestionnaireResponse", {
    subject: `Patient/${clientId}`,
    encounter: `Encounter/${encounterId}`,
    questionnaire: `Questionnaire/${questionnaireId}`,
    _sort: "-_lastUpdated",
  });
};

/**
 * Marks an existing questionnaire response as amended if it's not already amended
 * This eliminates the repetitive amendment logic across service functions
 */
export const markQuestionnaireResponseAsAmended = async (
  medplum: MedplumClient,
  existingResponse: QuestionnaireResponse,
): Promise<void> => {
  if (existingResponse.status !== "amended") {
    await medplum.updateResource({
      ...existingResponse,
      status: "amended",
    });
  }
};
